import { get, post } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { s3GetFolderFiles, _s3GetURL } from "lib/awsLib";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import Select from "react-select";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function WalkCopyList({ isAuthenticated, user }) {
  const walkCopyIdPrefix = "walk-copy-list";

  const [, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);
  const [selectedCityscope, setSelectedCityscope] = useState(null);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [cityscope, setCityscope] = useState(null);

  const createId = useMemo(
    () => createElementIdFactory(walkCopyIdPrefix),
    [walkCopyIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  // --- API Functions ---
  const getData = useCallback(async (cityscope) => {

    if (!cityscope) return;

    try {
      const prefixCandidates = [
        `public/WalkCopyPDF/${cityscope}`,
      ];
      const mergedItems = [];

      for (const prefix of prefixCandidates) {
        const s3files = await s3GetFolderFiles(prefix);

        if (s3files?.items && Array.isArray(s3files.items)) {
          mergedItems.push(...s3files.items);
        }
      }

      if (mergedItems.length > 0) {
        const files = [];
        const seen = new Set();

        for (const file of mergedItems) {
          const key = file?.key || "";
          if (!key || key.endsWith("/")) {
            continue;
          }

          const normalizedKey = key.replace(/^public\//, "");
          if (seen.has(normalizedKey)) {
            continue;
          }
          seen.add(normalizedKey);

          let url = null;
          try {
            url = await _s3GetURL(key, 900);
          } catch (error) {
            try {
              if (normalizedKey !== key) {
                url = await _s3GetURL(normalizedKey, 900);
              } else {
                url = await _s3GetURL(`public/${normalizedKey}`, 900);
              }
            } catch (fallbackError) {
              continue;
            }
          }

          files.push({
            key: normalizedKey.replace(`WalkCopyPDF/${cityscope}/`, ""),
            url,
            lastModified: String(file.lastModified),
          });
        }

        setData(files.reverse() || []);
        return true;
      }

      setData([]);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const getCityscopes = useCallback(async () => {
    try {
      const response = await get({
        apiName: "colin",
        path: "/cityscopes/dropdown",
      }).response;
      const cs = await response.body.json();
      const options = Array.isArray(cs)
        ? cs
        : cs?.items || cs?.data || cs?.results || cs?.cityscopes || [];

      return options
        .map((option) => {
          if (typeof option === "string") {
            return { value: option, label: option };
          }

          const value = option?.value ?? option?.cityscope ?? option?.code ?? "";
          const label = option?.label ?? option?.cityscope_name ?? option?.name ?? String(value);

          return value ? { ...option, value, label } : null;
        })
        .filter(Boolean);
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
      return [];
    }
  }, []);

  async function generateWalkCopies(cityscope) {
    const response = await post({
      apiName: "colin",
      path: `/walkcopy/${cityscope}`,
      options: {
        body: {},
      },
    }).response;

    const statusCode = Number(
      response?.statusCode ?? response?.status ?? response?.$metadata?.httpStatusCode ?? 200
    );

    if (statusCode >= 400) {
      throw new Error(`Walk copy generation request failed with status ${statusCode}`);
    }

    try {
      const payload = await response.body.json();
      if (payload && payload.result === false) {
        throw new Error(payload.error_message || "Walk copy generation failed on the server.");
      }
      return payload || true;
    } catch (error) {
      // Some successful endpoints return an empty body.
      return true;
    }
  }

  useEffect(() => {
    setIsLoading(true);
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localCityscopeOptions = await getCityscopes();
        setCityscopeOptions(localCityscopeOptions || []);
      } catch (e) {
        alert(e);
        setUi({ showAlert: true, errorMessage: "Failed to load data." });
      }
      setIsLoading(false);
    })();
  }, [isAuthenticated, getCityscopes]);

  const handleCityscopeChange = (selectedOption) => {
    setSelectedCityscope(selectedOption || null);
    setCityscope(selectedOption?.value || null);
  };

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!cityscope) {
      return;
    }

    setIsLoading(true);
    try {
      await getData(cityscope);
    } catch (e) {
      setUi({ showAlert: true, errorMessage: "Failed to load walk copies." });
    } finally {
      setIsLoading(false);
    }
  }, [cityscope, getData]);

  const handleGenerate = useCallback(async (event) => {
    event.preventDefault();
    if (!cityscope) {
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to generate a Walk Copy for ${cityscope} Cityscope?`);
    if (confirmed) {
      setIsLoading(true);
      window.alert("Your request to generate a walk copy has been confirmed! Your walk copy will be generated on the Server and should be available for download within 10 minutes. Please come back to this screen at a later time and click [Search] to view and download you walk copy (zip).");
      try {
        await generateWalkCopies(cityscope);
      } catch (e) {
        // Fail silently so the error is only visible in the network tab/dev tools.
      } finally {
        setIsLoading(false);
      }
    }
  }, [cityscope]);

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("key", {
      id: "file",
      header: "File",
      size: 260,
      // Display the file name, link to the S3 URL
      cell: ({ row }) => {
        const file = row.original;
        return (
          <a href={file.url} target="_blank" rel="noopener noreferrer">
            {file.key}
          </a>
        );
      },
    }),
    columnHelper.accessor("lastModified", {
      id: "lastModified",
      header: "Created Date",
      size: 180,
      cell: ({ row }) => row.original.lastModified,
    }),
  ], []);

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Form onSubmit={handleSubmit} className="mb-3">
        <Row className="g-2 align-items-start">
          <Col lg={2}>
            <Form.Group controlId="cityscope">
              <Select
                inputId={createId("cityscope", "select")}
                value={selectedCityscope}
                onChange={handleCityscopeChange}
                options={cityscopeOptions}
                placeholder="Cityscope"
                isClearable
              />
            </Form.Group>
          </Col>
          <Col lg="auto">
            {selectedCityscope && (
              <div className="d-flex gap-2">
                <Button id={createId("search", "button")} type="submit">Search</Button>
                <Button id={createId("generate", "button")} type="button" variant="primary" onClick={handleGenerate}>Generate</Button>
              </div>
            )}
          </Col>
        </Row>
      </Form>

      <Row>
        <Col>
          <div className="custom-data-table">
            <GenericTable
              columns={columns}
              data={data}
              section={walkCopyIdPrefix}
              elementIds={tableElementIds}
              pageSize={PAGE_SIZES.walkCopy}
              enablePagination={true}
              state={{ sorting }}
              onSortingChange={setSorting}
              enableSortingRemoval={false}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default WalkCopyList;